import { mockDelay } from '@/services/factory'
import type {
  BusinessEntitySummary,
  BusinessPortalRole,
  BusinessWorkspace,
  RepresentativeGrant,
} from '@/types/businessPortal.types'
import type { BusinessPortalService } from './businessPortal.service'

const entities: BusinessEntitySummary[] = [
  {
    id: '101', name: 'Kaveri Precision Works Pvt Ltd', tradeName: 'Kaveri Works',
    businessType: 'pvt_ltd', gstin: '27AABCK1234F1Z5', pan: 'AABCK1234F',
    tan: 'PNEK12345A', email: 'finance@kaveriworks.in', status: 'active',
    accessKind: 'membership',
  },
  {
    id: '102', name: 'Sahyadri Foods LLP', businessType: 'llp',
    gstin: '27AAVFS6789G1Z2', pan: 'AAVFS6789G', tan: 'PNES67890B',
    email: 'accounts@sahyadrifoods.in', status: 'active', accessKind: 'membership',
  },
]

let grants: RepresentativeGrant[] = [
  {
    id: 'grant-active', customerType: 'business', customerId: '101',
    granteeUserId: '00000000-0000-0000-0000-000000000202',
    grantorUserId: '00000000-0000-0000-0000-000000000201',
    actions: ['txh.client.read', 'txh.document.read', 'txh.gst_assessment.read'],
    status: 'accepted', effectiveAt: '2026-09-01T09:00:00Z',
    expiresAt: '2026-12-31T23:59:59Z', evidenceReference: 'Board resolution BR-18',
  },
  {
    id: 'grant-pending', customerType: 'business', customerId: '102',
    granteeUserId: '00000000-0000-0000-0000-000000000202',
    grantorUserId: '00000000-0000-0000-0000-000000000201',
    actions: ['txh.client.read', 'txh.document.upload', 'txh.gst_assessment.read'],
    status: 'pending', effectiveAt: '2026-09-13T09:00:00Z',
    expiresAt: '2026-11-30T23:59:59Z', evidenceReference: 'GST filing authorization FY 2026-27',
  },
]

function workspace(customerId: string): BusinessWorkspace {
  const second = customerId === '102'
  return {
    gst: [
      { id: 'gst-1', title: 'GSTR-3B', subtitle: 'August 2026', status: second ? 'draft' : 'filed', dueDate: '2026-09-20' },
      { id: 'gst-2', title: 'GSTR-1', subtitle: 'August 2026', status: 'in_review', dueDate: '2026-09-11' },
    ],
    incomeTax: [{ id: 'itr-1', title: 'ITR-6', subtitle: 'AY 2026-27', status: 'preparation' }],
    tds: [{ id: 'tds-1', title: 'TDS return 26Q', subtitle: 'Q2 FY 2026-27', status: 'documents_due', dueDate: '2026-10-31' }],
    notices: [{ id: 'notice-1', title: 'GST information request', subtitle: 'Reference GST-N-2048', status: 'response_due', dueDate: '2026-09-25' }],
    documents: [
      { id: 'doc-1', title: 'Purchase register - August', status: 'verified' },
      { id: 'doc-2', title: 'Bank statement - August', status: 'uploaded' },
    ],
    deadlines: [
      { id: 'due-1', title: 'GSTR-3B filing', status: 'upcoming', dueDate: '2026-09-20' },
      { id: 'due-2', title: 'TDS working papers', status: 'action_required', dueDate: '2026-10-15' },
    ],
    payments: [
      { id: 'pay-1', title: 'Monthly compliance retainer', status: 'completed', amount: second ? 18000 : 25000, currency: 'INR' },
      { id: 'pay-2', title: 'Notice response professional fee', status: 'pending', amount: 7500, currency: 'INR' },
    ],
  }
}

export function createMockBusinessPortalService(): BusinessPortalService {
  return {
    async getAccessContext(role: BusinessPortalRole) {
      await mockDelay(80)
      const activeIds = new Set(
        grants.filter((item) => item.status === 'accepted').map((item) => item.customerId)
      )
      const visible = role === 'business_owner_client'
        ? entities
        : entities.filter((item) => activeIds.has(item.id)).map((item) => ({
          ...item, accessKind: 'representative_grant' as const,
        }))
      return {
        role, entities: visible,
        activeGrants: grants.filter((item) => item.status === 'accepted'),
        scopeRefreshSeconds: 1800,
      }
    },

    async listGrants() {
      await mockDelay(60)
      return grants.map((item) => ({ ...item, actions: [...item.actions] }))
    },

    async loadWorkspace(customerId) {
      await mockDelay(90)
      return workspace(customerId)
    },

    async createGrant(input) {
      await mockDelay(60)
      const created: RepresentativeGrant = {
        id: `grant-${Date.now()}`, customerType: 'business',
        customerId: input.customerId, granteeUserId: input.granteeUserId,
        grantorUserId: '00000000-0000-0000-0000-000000000201',
        actions: [...input.actions], status: 'pending',
        effectiveAt: new Date().toISOString(), expiresAt: input.expiresAt,
        evidenceReference: input.evidenceReference,
      }
      grants = [created, ...grants]
      return created
    },

    async decideGrant(grantId, decision, note) {
      await mockDelay(60)
      grants = grants.map((item) => item.id === grantId ? {
        ...item, status: decision === 'accept' ? 'accepted' : 'declined',
        decisionNote: note,
      } : item)
      const updated = grants.find((item) => item.id === grantId)
      if (!updated) throw new Error('Grant not found')
      return updated
    },

    async revokeGrant(grantId) {
      await mockDelay(60)
      grants = grants.map((item) => item.id === grantId ? {
        ...item, status: 'revoked', revokedAt: new Date().toISOString(),
      } : item)
      const updated = grants.find((item) => item.id === grantId)
      if (!updated) throw new Error('Grant not found')
      return updated
    },
  }
}
